import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Tooltip léger CSS-only - pas de librairie externe.
 *
 * Apparaît au hover (pointer) et au focus clavier (ex. `<button>` enfant).
 * Accessibilité : `role="tooltip"` + `aria-describedby` géré par le parent si
 * besoin, sinon fallback sur `title` via `aria-label` si le child est textuel.
 *
 * Pour les cas où le tooltip doit s'ouvrir même sur un enfant non-focusable
 * (ex. `<span>`), on passe `tabIndex={0}` sur le wrapper pour rendre le
 * trigger accessible clavier sans changer son affichage.
 */
export function Tooltip({
  content,
  children,
  side = 'bottom',
  align = 'center',
  className,
}: {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom';
  align?: 'center' | 'start' | 'end';
  className?: string;
}) {
  return (
    <span
      tabIndex={0}
      // `group/tooltip` = variant Tailwind nommé : scope le hover à CE span
      // uniquement. Sans le suffixe, `group-hover:` remonterait au `.group`
      // le plus proche - typiquement la ligne de DataTable, ce qui
      // déclencherait le tooltip dès qu'on hover la ligne entière.
      className={cn('group/tooltip relative inline-flex outline-none', className)}
    >
      {children}
      <span
        role="tooltip"
        className={cn(
          // Masqué par défaut, visible au hover ou au focus clavier
          // uniquement sur le span wrapper (scope `/tooltip`).
          'pointer-events-none invisible absolute z-30 w-max max-w-xs whitespace-normal rounded-md bg-brand px-2.5 py-1.5 text-[11px] font-normal leading-snug text-white shadow-popover opacity-0 transition-opacity duration-150',
          'group-hover/tooltip:visible group-hover/tooltip:opacity-100 group-focus-visible/tooltip:visible group-focus-visible/tooltip:opacity-100',
          side === 'bottom' && 'top-full mt-1.5',
          side === 'top' && 'bottom-full mb-1.5',
          align === 'center' && 'left-1/2 -translate-x-1/2',
          align === 'start' && 'left-0',
          align === 'end' && 'right-0',
        )}
      >
        {content}
      </span>
    </span>
  );
}
